import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAuth } from '../../contexts/AuthContext'
import { getCredits, CreditSummary, createPortalSession } from '../../services/paymentApi'
import { getMyForms, UserForm, exportMyData, updateProfile } from '../../services/authApi'
import { deleteForm } from '../../services/api'
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate'

export default function Account() {
  const { t, i18n } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { user, credits: creditCount, subscription, loading: authLoading, deleteAccount, refresh } = useAuth()
  const [searchParams] = useSearchParams()
  const [creditData, setCreditData] = useState<CreditSummary | null>(null)
  const [myForms, setMyForms] = useState<UserForm[]>([])
  const [, setLoading] = useState(true)
  const [editingName, setEditingName] = useState(false)
  const [editName, setEditName] = useState('')
  const [savingName, setSavingName] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const deleteInputRef = useRef<HTMLInputElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { document.title = `${t('account.title')} | ${t('common.appName')}` }, [t])

  const justSubscribed = searchParams.get('subscribed') === 'true'
  const locale = i18n.language === 'he' ? 'he-IL' : 'en-US'

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      navigate('/login?redirect=/account')
      return
    }
    loadData()
  }, [user, authLoading])

  // Scroll to hash target (e.g., #my-documents) after data loads
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash)
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100)
      }
    }
  }, [myForms])

  const loadData = async () => {
    try {
      const [creditsData, formsData] = await Promise.all([
        getCredits(),
        getMyForms(),
      ])
      setCreditData(creditsData)
      setMyForms(formsData)
    } catch {
      // Silent fail
    } finally {
      setLoading(false)
    }
  }

  const handleManageBilling = async () => {
    try {
      const { portalUrl } = await createPortalSession()
      window.location.href = portalUrl
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const handleSaveName = async () => {
    if (!editName.trim()) return
    setSavingName(true)
    try {
      await updateProfile(editName.trim())
      await refresh()
      setEditingName(false)
    } catch {
      alert(t('account.editNameError'))
    } finally {
      setSavingName(false)
    }
  }

  const handleDeleteForm = async (formId: string, formTitle: string) => {
    if (!confirm(t('account.deleteFormConfirm', { title: formTitle }))) return
    try {
      await deleteForm(formId)
      setMyForms(myForms.filter((f) => f.id !== formId))
    } catch {
      alert(t('account.deleteFormError'))
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return
    setDeleting(true)
    try {
      await deleteAccount()
      navigate('/')
    } catch {
      alert(t('account.deleteError'))
      setDeleting(false)
    }
  }

  if (!user) return null

  return (
    <div className="container">
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        <div className="page-header">
          <button className="btn btn-outline btn-icon" onClick={() => navigate('/')} aria-label={t('common.back')}>
            <FontAwesomeIcon icon="arrow-left" />
          </button>
          <h1>{t('account.title')}</h1>
        </div>

        {justSubscribed && (
          <div className="card mb-4" style={{ background: '#f0fdf4', border: '1px solid #22c55e' }} role="status">
            <p style={{ color: '#15803d' }}>
              <FontAwesomeIcon icon="check" style={{ marginInlineEnd: '0.5rem' }} />
              {t('account.subscriptionSuccess')}
            </p>
          </div>
        )}

        {/* Profile */}
        <div className="card mb-4">
          <h3>{t('account.profile')}</h3>
          <div className="account-info">
            <div className="account-row">
              <span className="text-gray">{t('account.name')}</span>
              {editingName ? (
                <div className="flex gap-2 items-center">
                  <input
                    ref={nameInputRef}
                    type="text"
                    className="form-input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false) }}
                    style={{ width: '160px', padding: '0.375rem 0.5rem', fontSize: '0.875rem' }}
                  />
                  <button className="btn btn-primary" onClick={handleSaveName} disabled={savingName} style={{ padding: '0.375rem 0.75rem', fontSize: '0.8rem' }}>
                    {savingName ? <FontAwesomeIcon icon="spinner" className="spinner" /> : t('common.save')}
                  </button>
                  <button className="btn btn-outline" onClick={() => setEditingName(false)} style={{ padding: '0.375rem 0.75rem', fontSize: '0.8rem' }}>
                    {t('common.cancel')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline"
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', fontWeight: 500 }}
                  onClick={() => { setEditName(user.displayName); setEditingName(true); setTimeout(() => nameInputRef.current?.focus(), 50) }}
                >
                  {user.displayName} <FontAwesomeIcon icon="pen" style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginInlineStart: '0.375rem' }} />
                </button>
              )}
            </div>
            <div className="account-row">
              <span className="text-gray">{t('account.email')}</span>
              <span>{user.email}</span>
            </div>
            <div className="account-row">
              <span className="text-gray">{t('account.provider')}</span>
              <span style={{ textTransform: 'capitalize' }}>{user.provider}</span>
            </div>
          </div>
          <button
            className="btn btn-outline mt-4"
            onClick={async () => {
              try { await exportMyData() } catch { alert(t('account.exportError')) }
            }}
          >
            <FontAwesomeIcon icon="download" />
            {t('account.exportData')}
          </button>
        </div>

        {/* Credits */}
        <div className="card mb-4">
          <h3>{t('account.credits')}</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="stat-box stat-box-success">
              <div className="stat-number">{creditData?.summary.available ?? creditCount}</div>
              <div className="stat-label">{t('account.available')}</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{creditData?.summary.used ?? 0}</div>
              <div className="stat-label">{t('account.used')}</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">{creditData?.summary.expired ?? 0}</div>
              <div className="stat-label">{t('account.expired')}</div>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/pricing')}>
            <FontAwesomeIcon icon="credit-card" />
            {t('account.getMoreCredits')}
          </button>
        </div>

        {/* My Documents */}
        <div id="my-documents" className="card mb-4">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>{t('account.myDocuments')}</h3>
            <button className="btn btn-primary" onClick={() => navigate('/create')}>
              <FontAwesomeIcon icon="plus" />
              {t('home.createNew')}
            </button>
          </div>
          {myForms.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon="file" />
              <p>{t('account.noDocuments')}</p>
            </div>
          ) : (
            <div>
              {myForms.map((form) => (
                <div
                  key={form.id}
                  className="link-item"
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/dashboard/${form.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/dashboard/${form.id}`) } }}
                >
                  <div>
                    <strong>{form.title}</strong>
                    <div className="text-sm text-gray">
                      {new Date(form.createdAt).toLocaleDateString(locale)}
                      {' · '}
                      <span style={{ color: form.isActivated ? 'var(--success)' : 'var(--warning)' }}>
                        {form.isActivated ? t('account.active') : t('activation.title')}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <code className="text-sm" dir="ltr" style={{ color: 'var(--gray-500)' }}>{form.id.slice(0, 8)}...</code>
                    <button
                      className="btn btn-outline btn-icon"
                      style={{ color: 'var(--danger)', borderColor: 'var(--danger)', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                      onClick={(e) => { e.stopPropagation(); handleDeleteForm(form.id, form.title) }}
                      aria-label={t('account.deleteForm')}
                    >
                      <FontAwesomeIcon icon="trash" />
                    </button>
                    <FontAwesomeIcon icon="arrow-right" className="text-gray" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Subscription */}
        <div className="card mb-4">
          <h3>{t('account.subscription')}</h3>
          {subscription?.active ? (
            <div>
              <div className="account-row mb-2">
                <span className="text-gray">{t('account.plan')}</span>
                <span className="badge badge-success">
                  {subscription.planId === 'plan_35' ? t('pricing.starterName') : t('pricing.proName')}
                </span>
              </div>
              <div className="account-row mb-4">
                <span className="text-gray">{t('account.status')}</span>
                <span className="badge badge-success">{t('account.active')}</span>
              </div>
              <button className="btn btn-outline" onClick={handleManageBilling}>
                {t('account.manageBilling')}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-gray mb-4">{t('account.noSubscription')}</p>
              <button className="btn btn-primary" onClick={() => navigate('/pricing')}>
                {t('account.subscribeCta')}
              </button>
            </div>
          )}
        </div>

        {/* Delete Account */}
        <div className="card mb-4" style={{ borderColor: 'var(--danger)' }}>
          <h3 style={{ color: 'var(--danger)' }}>{t('account.deleteAccount')}</h3>
          <p className="text-gray text-sm mb-4">{t('account.deleteWarning')}</p>
          {!showDeleteConfirm ? (
            <button
              className="btn btn-outline"
              style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
              onClick={() => {
                setShowDeleteConfirm(true)
                setTimeout(() => deleteInputRef.current?.focus(), 100)
              }}
            >
              <FontAwesomeIcon icon="trash" />
              {t('account.deleteAccount')}
            </button>
          ) : (
            <div>
              <p className="text-sm mb-2" style={{ color: 'var(--danger)' }}>
                {t('account.deleteConfirmPrompt')}
              </p>
              <input
                ref={deleteInputRef}
                type="text"
                className="input mb-3"
                placeholder="DELETE"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                dir="ltr"
              />
              <div className="flex gap-2">
                <button
                  className="btn"
                  style={{
                    background: 'var(--danger)',
                    color: 'white',
                    opacity: deleteConfirmText !== 'DELETE' ? 0.5 : 1,
                  }}
                  disabled={deleteConfirmText !== 'DELETE' || deleting}
                  onClick={handleDeleteAccount}
                >
                  {deleting ? t('common.loading') : t('account.confirmDelete')}
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setDeleteConfirmText('')
                  }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
